import {ColorSchemeContext} from '@common/core/color-scheme-provider';
import {LocaleSwitcher} from '@common/locale-switcher/locale-switcher';
import {useCustomMenu} from '@common/menus/use-custom-menu';
import {
  SiFacebook,
  SiInstagram,
  SiTiktok,
  SiX,
  SiYoutube,
} from '@icons-pack/react-simple-icons';
import {Button} from '@shadcn/button/button';
import {Trans} from '@ui/i18n/trans';
import {useSettings} from '@ui/settings/use-settings';
import clsx from 'clsx';
import {MoonIcon, SunIcon} from 'lucide-react';
import {use} from 'react';
import {UnstyledCustomMenuItem} from '../../menus/custom-menu';

interface Props {
  className?: string;
  padding?: string;
}

export function Footer({className, padding}: Props) {
  const year = new Date().getFullYear();
  const {branding} = useSettings();
  return (
    <footer
      className={clsx(
        'text-sm',
        padding ? padding : 'pt-12 pb-6 md:pb-12',
        className,
      )}
    >
      <Menus />
      <p className="mt-6 w-full text-center text-muted-foreground">
        <Trans
          message="Copyright © :year :name, All Rights Reserved"
          values={{year, name: branding.site_name}}
        />
      </p>
    </footer>
  );
}

function Menus() {
  const primaryMenu = useCustomMenu('footer');
  const secondaryMenu = useCustomMenu('footer-secondary');

  return (
    <div className="mb-3.5 items-start justify-between gap-7.5 overflow-x-auto border-b pb-3.5 md:flex">
      {primaryMenu && (
        <div className="flex items-center gap-3 text-primary">
          {primaryMenu.items.map(item => (
            <UnstyledCustomMenuItem
              key={item.id}
              item={item}
              className="hover:underline"
            />
          ))}
        </div>
      )}
      {secondaryMenu && (
        <div className="flex items-center gap-5 text-muted-foreground [&_svg:not([class*='size-'])]:size-4">
          {secondaryMenu.items.map(item => {
            const icon =
              typeof item.icon === 'string' ? (
                <SocialIcon icon={item.icon} />
              ) : null;
            return (
              <UnstyledCustomMenuItem key={item.id} item={item} icon={icon} />
            );
          })}
        </div>
      )}
      <div className="flex shrink-0 items-start gap-5">
        <SocialMediaLinks />
        <div className="flex flex-col items-end gap-2">
          <ThemeSwitcher />
          <LocaleSwitcher />
        </div>
      </div>
    </div>
  );
}

const SOCIAL_MEDIA_LINKS = [
  {name: 'Facebook', href: '#', Icon: SiFacebook},
  {name: 'X', href: '#', Icon: SiX},
  {name: 'YouTube', href: '#', Icon: SiYoutube},
  {name: 'TikTok', href: '#', Icon: SiTiktok},
  {name: 'Instagram', href: '#', Icon: SiInstagram},
] as const;

export function SocialMediaLinks({className}: {className?: string}) {
  return (
    <div className={clsx('flex flex-row items-center justify-center gap-4 md:flex-col md:items-start md:justify-start', className)}>
      {SOCIAL_MEDIA_LINKS.map(({name, href, Icon}) => (
        <a
          key={name}
          href={href}
          aria-label={name}
          className="cursor-pointer text-gray-400 transition-colors hover:text-white"
        >
          <Icon className="size-5" />
        </a>
      ))}
    </div>
  );
}

function SocialIcon({icon}: {icon: string}) {
  switch (icon) {
    case 'facebook':
      return <SiFacebook />;
    case 'twitter':
      return <SiX />;
    case 'instagram':
      return <SiInstagram />;
    case 'youtube':
      return <SiYoutube />;
    case 'tiktok':
      return <SiTiktok />;
    default:
      return null;
  }
}

function ThemeSwitcher() {
  const {themes} = useSettings();
  const {colorScheme, setColorScheme} = use(ColorSchemeContext);
  if (!themes?.user_change) return null;

  return (
    <Button
      variant="ghost"
      onClick={() => {
        setColorScheme(colorScheme === 'light' ? 'dark' : 'light');
      }}
    >
      {colorScheme === 'dark' ? (
        <>
          <SunIcon />
          <Trans message="Light mode" />
        </>
      ) : (
        <>
          <MoonIcon />
          <Trans message="Dark mode" />
        </>
      )}
    </Button>
  );
}
